<script>
  
    import { onMount } from 'svelte';
    import { csvParse } from 'd3';
    import dataUrl from './data/city_snowfall.csv?url';

    import { groups, scaleLinear, mean } from 'd3';


    // `data` is wired through from App.svelte (loaded from src/data/data.json)
    let { width = 600 } = $props();

    const height = 250;
    let data = $state([]);

    onMount(async() => {
        const req = await fetch(dataUrl);
        const text = await req.text();   // raw CSV text content
        data = csvParse(text);           // parse into array of row objects
    });
    const groupedData = $derived(groups(data, d => d.city));

    // One row per city, with its averages + diff precomputed, sorted by the
    // biggest absolute change (niño vs. all-winter average) first. Cities with
    // no niño winters (diff === null) fall to the bottom.
    const sortedData = $derived(
        groupedData
            .map(([cityName, winters]) => {
                const avgAll = mean(winters, d => +d['snowfall total']);
                const avgNino = mean(
                    winters.filter(w => w['Niño'] === 'TRUE'),
                    d => +d['snowfall total']
                );
                const diff = avgAll != null && avgNino != null ? avgNino - avgAll : null;
                return { cityName, winters, avgAll, avgNino, diff };
            })
            .sort((a, b) => {
                if (a.diff == null) return 1;
                if (b.diff == null) return -1;
                return Math.abs(b.diff) - Math.abs(a.diff);
            })
    );

    const maxData = $derived(Math.max(...data.map(d => +d['snowfall total'])));

    const svgWidth = 500;
    const svgHeight = 20;
    const margin = {left: 10, right: 10};
    const chartwidth = svgWidth - margin.left - margin.right;

    const scale = $derived(maxData > 0 ? 
        scaleLinear().domain([0, 100]).range([0, chartwidth]) :
        null
    )

    // Preferred display spellings; cities not listed fall back to their raw name.
    const CITY_LABELS = {
        'Anchorage': 'Anchorage, AK',
        'Austin': 'Austin, TX',
        'Charlotte': 'Charlotte, NC',
        'Columbus OH': 'Columbus, OH',
        'Jacksonville': 'Jacksonville, FL',
        'Kansas City': 'Kansas City, MO',
        'Portland OR': 'Portland, OR',
        'Raleigh': 'Raleigh, NC',
        'Washington DC': 'Washington, DC'
    };

    // Which curated category each city belongs to. Cities not listed here are
    // dropped from the display.
    const CITY_CATEGORIES = {
        // decreased
        'Anchorage': 'decreased',
        'Minneapolis': 'decreased',
        'Chicago': 'decreased',
        'Detroit': 'decreased',
        'Las Vegas': 'decreased',
        'Indianapolis': 'decreased',
        // increased
        'Baltimore': 'increased',
        'Washington DC': 'increased',
        'Philadelphia': 'increased',
        'Denver': 'increased',
        'Dallas': 'increased',
        'Nashville': 'increased',
        'Kansas City': 'increased',
        'Boston': 'increased',
        'New York': 'increased',
        'Atlanta': 'increased',
        // didn't change much
        'Portland OR': 'same',
        'Raleigh': 'same',
        'Los Angeles': 'same'
    };

    // Category sections, in display order, each with its matching rows (kept in
    // the existing biggest-change-first sort order).
    const CATEGORY_GROUPS = [
        { id: 'decreased', label: 'Less snowy El Niño winters' },
        { id: 'increased', label: 'Snowier El Niño winters' },
        { id: 'same', label: 'Not much change' }
    ];
    const groupedByCategory = $derived(
        CATEGORY_GROUPS.map(group => ({
            ...group,
            rows: sortedData.filter(d => CITY_CATEGORIES[d.cityName] === group.id)
        }))
    );

    $inspect(groupedData);
</script>


<!-- Shared arrowhead markers (referenced by every row's arrow) -->
<svg class="defs-only" width="0" height="0" aria-hidden="true">
    <defs>
        <marker id="arrow-more" markerUnits="userSpaceOnUse" markerWidth="11" markerHeight="11"
                viewBox="0 0 10 10" refX="7" refY="5" orient="auto">
            <path d="M0,1 L9,5 L0,9 z" fill="#394461" />
        </marker>
        <marker id="arrow-less" markerUnits="userSpaceOnUse" markerWidth="11" markerHeight="11"
                viewBox="0 0 10 10" refX="7" refY="5" orient="auto">
            <path d="M0,1 L9,5 L0,9 z" fill="#c99400" />
        </marker>
    </defs>
</svg>

<div class="legend">
    <div class="legend-item">
        <svg class="swatch" viewBox="0 0 14 14" aria-hidden="true">
            <circle cx="7" cy="7" r="3" class="winter-circle" />
        </svg>
        <span>Winter (non–El Niño)</span>
    </div>
    <div class="legend-item">
        <svg class="swatch" viewBox="0 0 14 14" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" class="winter-circle nino" />
        </svg>
        <span>El Niño winter</span>
    </div>
    <div class="legend-item">
        <svg class="swatch" viewBox="0 0 14 14" aria-hidden="true">
            <line x1="7" x2="7" y1="1" y2="13" class="avg-line avg-all" />
        </svg>
        <span>Average, all winters</span>
    </div>
    <div class="legend-item">
        <svg class="swatch" viewBox="0 0 14 14" aria-hidden="true">
            <line x1="5" x2="5" y1="1" y2="13" class="avg-line more" />
            <line x1="9" x2="9" y1="1" y2="13" class="avg-line less" />
        </svg>
        <span>Average, El Niño winters</span>
    </div>
</div>
<p class="data-label">Average difference in inches of snowfall between El Niño winters and all winters</p>
<div class="chart">
    {#each groupedByCategory as group, gi}
        <div class="group">
            <h3 class="group-label">{group.label}</h3>
            {#each group.rows as { cityName, winters, avgAll, avgNino, diff }, i (cityName)}
                {@const isFirstRow = gi === 0 && i === 0}
                {@const isSame = group.id === 'same'}
                <div class="row">
            <div class="city">{CITY_LABELS[cityName] ?? cityName}</div>
            <div class="viz">
                {#if scale && diff != null}
                    {@const labelPct = (margin.left + (scale(avgAll) + scale(avgNino)) / 2) / svgWidth * 100}
                    <div
                        class="diff-label"
                        class:more={!isSame && diff > 0}
                        class:less={!isSame && diff < 0}
                        class:edge-left={labelPct < 20}
                        class:edge-right={labelPct > 80}
                        style="left: {labelPct}%;"
                    >
                        {#if isSame}+0 in{:else}{diff > 0 ? '+' : ''}{diff.toFixed(1)} in{#if isFirstRow}ches difference in El Niño winters{/if}{/if}
                    </div>
                {/if}
                <svg viewBox="0 0 {svgWidth} {svgHeight}" preserveAspectRatio="xMinYMid meet">

                            <line x1={margin.left} 
                                x2={svgWidth - margin.right} 
                                y1={svgHeight / 2} 
                                y2={svgHeight / 2} 
                                stroke-width=1 
                                class="axis"
                            >
                            </line>
                        
                        <g transform="translate({margin.left}, {svgHeight / 2})">

                            {#if scale && isFirstRow}
                                {#each [0, 100] as t}
                                    <line class="axis-tick" x1={scale(t)} x2={scale(t)} y1={-svgHeight / 4} y2={svgHeight / 4} />
                                {/each}
                            {/if}
                            {#if scale}
                                {#each [...winters].sort((a, b) => (a['Niño'] === 'TRUE') - (b['Niño'] === 'TRUE')) as winter}
                                    {@const cx = scale(+winter['snowfall total'])}
                                    {@const cy = 0}
                                    <circle
                                        class="winter-circle"
                                        class:nino={winter['Niño'] === 'TRUE'}
                                        {cx}
                                        {cy}
                                        r={winter['Niño'] === 'TRUE' ? 5 : 3}
                                    >

                                    </circle>
                                {/each}
                                {#if avgAll != null}
                                    <line
                                        class="avg-line avg-all"
                                        x1={scale(avgAll)}
                                        x2={scale(avgAll)}
                                        y1={-svgHeight / 2}
                                        y2={svgHeight / 2}
                                    />
                                {/if}
                                {#if avgNino != null}
                                    <line
                                        class="avg-line avg-nino"
                                        class:more={!isSame && diff > 0}
                                        class:less={!isSame && diff < 0}
                                        class:gray={isSame}
                                        x1={scale(avgNino)}
                                        x2={scale(avgNino)}
                                        y1={-svgHeight / 2}
                                        y2={svgHeight / 2}
                                    />
                                {/if}
                                {#if !isSame && diff != null && Math.abs(diff) >= 1}
                                    <line
                                        class="arrow"
                                        class:more={diff > 0}
                                        class:less={diff < 0}
                                        x1={scale(avgAll)}
                                        y1={0}
                                        x2={scale(avgNino)}
                                        y2={0}
                                        marker-end={diff > 0 ? 'url(#arrow-more)' : 'url(#arrow-less)'}
                                    />
                                {/if}
                            {/if}
                        </g>
                </svg>
                {#if isFirstRow}
                    <div class="axis-labels">
                        <span class="axis-text start" style="left: {margin.left / svgWidth * 100}%;">0</span>
                        <span class="axis-text end" style="left: {(svgWidth - margin.right) / svgWidth * 100}%;">100 in</span>
                    </div>
                {/if}
            </div>
        </div>
            {/each}
        </div>
    {/each}
</div>

<style lang='scss'>

.winter-circle {
    fill: $gray4;
    opacity: 0.7; // semi-transparent so overlapping dots read as denser

    &.nino {
        fill: #ffffff;    // red
        stroke: #999999;
        stroke-width: 1;
        // opacity: 0.5;     // more transparent so stacked niño dots read as denser
    }
}

.avg-line {
    stroke-width: 1.5;

    &.avg-all {
        stroke: $gray8;
    }
    &.avg-nino {
        stroke: #c99400; // fallback when there's no diff direction
    }
    // El Niño average is colored by direction: blue = more snow, orange = fewer
    &.more {
        stroke: #394461;
    }
    &.less {
        stroke: #c99400;
    }
    // "Not much change" section: keep the El Niño average line gray too
    &.gray {
        stroke: $gray8;
    }
}

.defs-only {
    position: absolute;
    width: 0;
    height: 0;
}

.arrow {
    stroke-width: 2;

    &.more {
        stroke: #394461; // El Niño winters average MORE snow (blue)
    }
    &.less {
        stroke: #c99400; // El Niño winters average LESS snow (orange)
    }
}

.legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin-bottom: 16px;
    font-size: 12px;
    color: $gray9;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.legend .swatch {
    flex: none;
    width: 14px;
    height: 14px;
    overflow: visible;
}

.chart {
    display: flex;
    flex-direction: column;
    row-gap: 28px; // space between category sections
}

.group {
    display: flex;
    flex-direction: column;
    row-gap: 10px; // space between rows within a section
}

.group-label {
    margin: 0 0 2px;
    font-size: 14px;
    font-weight: 700;
    color: $gray11;
}


.row {
    display: grid;
    grid-template-columns: 120px 1fr; // city label | viz
    align-items: center;
    column-gap: 12px;
}

.city {
    font-size: 13px;
    // font-weight: 700;
    color: $gray11;
    text-align: right;
    // Match the viz's top padding so the centered label lines up with the plot
    // (the dots sit below the viz's reserved label space), not the viz box center.
    padding-top: 15px;
}

.viz {
    position: relative;
    padding-top: 15px; // headroom for the absolutely-positioned label
    max-width: 500px;  // don't let the fluid svg stretch past its design size

    svg {
        display: block;
        width: 100%;
        height: auto; // scales with width via the viewBox
    }
}

// Mobile: stack the city label on top and let the viz go full width.
@media (max-width: 500px) {
    .row {
        grid-template-columns: 1fr;
        row-gap: 2px;
    }

    .viz {
        max-width: none;
    }

    .city {
        text-align: left;
        padding-top: 0;
    }
}

.axis-tick {
    stroke: $gray6;
    stroke-width: 1;
}

.axis-labels {
    // Positioned out of flow (just below the svg) so the first row doesn't
    // get taller than the others.
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 13px;
}

.axis-text {
    position: absolute;
    top: 0;
    font-size: 10px;
    color: $gray8;
    white-space: nowrap;

    // right-align the 100 in label so it doesn't bleed off the right edge
    &.end {
        transform: translateX(-100%);
    }
}

.axis {
    stroke: $gray4;
}

.diff-label {
    position: absolute;
    top: 0;
    transform: translateX(-50%); // center on the gap midpoint (left)
    white-space: nowrap;

    // Near the edges, anchor the label inward so it doesn't clip off-screen.
    &.edge-left {
        transform: translateX(0);
    }
    &.edge-right {
        transform: translateX(-100%);
    }
    font-size: 11px;
    font-weight: 700;
    color: $gray8;

    &.more {
        color: #394461; // niño winters get MORE snow
    }
    &.less {
        color:  #c99400; // niño winters get LESS snow
    }
}
  
</style>
